import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InstagramPostPreview from './components/InstagramPostPreview';
import FacebookPostPreview from './components/FacebookPostPreview';

// Componente para el estado inicial completo
const InitialView = ({ onConfirm, onDislike, isConfirmed, isSubmitting }: {
  onConfirm: () => void;
  onDislike: () => void;
  isConfirmed: boolean;
  isSubmitting: boolean;
}) => (
  <div className="flex flex-col justify-center">
    {/* Título principal - oculto en móviles */}
    <h1 className="hidden lg:block text-5xl font-bold text-[#44aaf6] mb-10 text-center" style={{letterSpacing: '0.02em'}}>
      ¡Te hemos preparado una publicación para hoy!
    </h1>

    {/* Texto explicativo */}
    <div className="mb-6">
      <p className="text-lg text-gray-700 leading-relaxed text-center mb-6">
        ¿Te parece adecuado el contenido? 
        Danos el visto bueno, y nosotros nos encargamos de publicarlo en tus redes al momento.
      </p>
    </div>

    {/* Botones de acción */}
    <div className="flex flex-col gap-6 justify-center items-center">
      <button 
        onClick={onConfirm}
        disabled={isConfirmed || isSubmitting}
        className={`px-20 py-4 font-bold text-xl rounded-full transition-all duration-300 shadow-lg hover:shadow-xl ${
          isConfirmed 
            ? 'bg-green-500 text-white cursor-not-allowed' 
            : isSubmitting
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-[#44aaf6] text-white hover:bg-blue-600'
        }`}
      >
        {isConfirmed ? 'Publicación Confirmada' : isSubmitting ? 'Publicando...' : 'Publicar'}
      </button>
      
      <button 
        onClick={onDislike}
        disabled={isConfirmed || isSubmitting}
        className={`px-20 py-4 font-bold text-xl rounded-full transition-all duration-300 shadow-lg hover:shadow-xl border-2 ${
          isConfirmed || isSubmitting
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300' 
            : 'bg-white text-[#44aaf6] border-[#44aaf6] hover:bg-[#44aaf6] hover:text-white'
        }`}
      >
        Cambiar
      </button>
    </div>
  </div>
);

// Componente para las opciones de feedback
const FeedbackOptions = ({ onImageFeedback, onTextFeedback, onBothFeedback, onNoPublish, onBack, isSubmitting }: {
  onImageFeedback: () => void;
  onTextFeedback: () => void;
  onBothFeedback: () => void;
  onNoPublish: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) => (
  <div className="text-center">
    <div className="flex items-center justify-center mb-6">
      <button
        onClick={onBack}
        className="mr-4 p-2 text-gray-600 hover:text-[#44aaf6] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h3 className="text-4xl font-bold text-[#44aaf6]">
        Publicación cancelada
      </h3>
    </div>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      No te preocupes, lo volvemos a intentar. ¿Qué es lo que no te convence?
    </p>
    <div className="flex flex-col gap-0 justify-center items-center mb-8">
      <button 
        onClick={onImageFeedback}
        className="px-8 py-4 text-xl text-gray-700 hover:text-[#44aaf6] cursor-pointer transition-all duration-300 hover:scale-110"
      >
        La imagen
      </button>
      <button 
        onClick={onTextFeedback}
        className="px-8 py-4 text-xl text-gray-700 hover:text-[#44aaf6] cursor-pointer transition-all duration-300 hover:scale-110"
      >
        El texto
      </button>
      <button 
        onClick={onBothFeedback}
        className="px-8 py-4 text-xl text-gray-700 hover:text-[#44aaf6] cursor-pointer transition-all duration-300 hover:scale-110"
      >
        Ambos
      </button>
      <button 
        onClick={onNoPublish}
        disabled={isSubmitting}
        className={`px-8 py-4 text-xl transition-all duration-300 ${
          isSubmitting
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-700 hover:text-[#44aaf6] cursor-pointer hover:scale-110'
        }`}
      >
        {isSubmitting ? 'Procesando...' : 'No quiero publicar nada hoy'}
      </button>
    </div>
  </div>
);

// Componente para el feedback de texto
const TextFeedback = ({ textFeedback, setTextFeedback, onSubmit, onBack, error, isSubmitting }: {
  textFeedback: string;
  setTextFeedback: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  error: string;
  isSubmitting: boolean;
}) => (
  <div className="text-center">
    <div className="flex items-center justify-center mb-6">
      <button
        onClick={onBack}
        className="mr-4 p-2 text-gray-600 hover:text-[#44aaf6] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h3 className="text-4xl font-bold text-[#44aaf6]">
        Descríbenos tu problema
      </h3>
    </div>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      Dinos qué es lo que cambiarías de la publicación, y nosotros nos encargamos de crear una nueva adaptándonos a tus especificaciones. Cuanto más concretes lo que quieres cambiar, ¡mejor será el resultado!
    </p>
    <div className="w-full max-w-2xl mx-auto mb-6">
      <textarea
        placeholder="Ej: El texto es demasiado formal, me gustaría algo más cercano. También quiero hacer más enfasis en las ofertas de primavera que ofrecemos: hasta un 50% en productos seleccionados..."
        value={textFeedback}
        onChange={(e) => {
          if (e.target.value.length <= 1000) setTextFeedback(e.target.value);
        }}
        maxLength={1000}
        className="w-full p-4 rounded-[2rem] bg-[#e8edf4] border-2 border-transparent focus:border-blue-300 focus:ring-0 outline-none h-40 resize-none placeholder-gray-400 text-gray-800 leading-relaxed"
      />
      {error && (
        <p className="text-red-500 text-sm mt-2 text-left">
          {error}
        </p>
      )}
    </div>
    <div className="flex flex-col gap-4 justify-center items-center mb-6">
      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className={`px-10 py-3 rounded-full border-2 text-lg font-semibold transition-all ${
          isSubmitting
            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
            : 'border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'
        }`}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar'}
      </button>
    </div>
  </div>
);

// Componente para el feedback de imagen
const ImageFeedback = ({ imageFeedback, setImageFeedback, onSubmit, onBack, error, isSubmitting }: {
  imageFeedback: string;
  setImageFeedback: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  error: string;
  isSubmitting: boolean;
}) => (
  <div className="text-center">
    <div className="flex items-center justify-center mb-6">
      <button
        onClick={onBack}
        className="mr-4 p-2 text-gray-600 hover:text-[#44aaf6] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h3 className="text-4xl font-bold text-[#44aaf6]">
        Descríbenos tu problema
      </h3>
    </div>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      Indícanos qué aspectos de la imagen quieres cambiar, y nos encargamos de editarla o crear una nueva adaptándonos a tus especificaciones. Cuanto más concretes lo que quieres, ¡mejor será el resultado!
    </p>
    <div className="w-full max-w-2xl mx-auto mb-6">
      <textarea
        placeholder="Ej: Esta imagen es demasiado general, quiero algo más enfocado en mi producto. También quiero colores más vivos y llamativos."
        value={imageFeedback}
        onChange={(e) => {
          if (e.target.value.length <= 1000) setImageFeedback(e.target.value);
        }}
        maxLength={1000}
        className="w-full p-4 rounded-[2rem] bg-[#e8edf4] border-2 border-transparent focus:border-blue-300 focus:ring-0 outline-none h-40 resize-none placeholder-gray-400 text-gray-800 leading-relaxed"
      />
      {error && (
        <p className="text-red-500 text-sm mt-2 text-left">
          {error}
        </p>
      )}
    </div>
    <div className="flex flex-col gap-4 justify-center items-center mb-6">
      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className={`px-10 py-3 rounded-full border-2 text-lg font-semibold transition-all ${
          isSubmitting
            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
            : 'border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'
        }`}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar'}
      </button>
    </div>
  </div>
);

// Componente para el primer paso del feedback de ambos (solo texto)
const BothTextStep = ({ bothTextFeedback, setBothTextFeedback, onNext, onBack, textError }: {
  bothTextFeedback: string;
  setBothTextFeedback: (value: string) => void;
  onNext: () => void;
  onBack: () => void;
  textError: string;
}) => (
  <div className="text-center">
    <div className="flex items-center justify-center mb-6">
      <button
        onClick={onBack}
        className="mr-4 p-2 text-gray-600 hover:text-[#44aaf6] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h3 className="text-4xl font-bold text-[#44aaf6]">
        Descríbenos tu problema
      </h3>
    </div>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      Dinos qué es lo que quieres cambiar del texto. Cuanto más detalles lo que quieres cambiar, ¡mejor será el resultado!
    </p>
    
    {/* Input para feedback de texto */}
    <div className="w-full max-w-2xl mx-auto mb-6">
      <label className="block text-left text-sm font-semibold text-gray-700 mb-2">
        ¿Qué cambiarías del texto?
      </label>
      <textarea
        placeholder="Ej: El texto es demasiado formal, me gustaría algo más cercano. También quiero hacer más enfasis en las ofertas de primavera que ofrecemos: hasta un 50% en productos seleccionados..."
        value={bothTextFeedback}
        onChange={(e) => {
          if (e.target.value.length <= 1000) setBothTextFeedback(e.target.value);
        }}
        maxLength={1000}
        className="w-full p-4 rounded-[2rem] bg-[#e8edf4] border-2 border-transparent focus:border-blue-300 focus:ring-0 outline-none h-40 resize-none placeholder-gray-400 text-gray-800 leading-relaxed"
      />
      {textError && (
        <p className="text-red-500 text-sm mt-2 text-left">
          {textError}
        </p>
      )}
    </div>

    <div className="flex flex-col gap-4 justify-center items-center mb-6">
      <button
        onClick={onNext}
        className="px-10 py-3 rounded-full border-2 border-[#44aaf6] text-[#44aaf6] text-lg font-semibold transition-all hover:bg-blue-50"
      >
        Siguiente
      </button>
    </div>
  </div>
);

// Componente para el segundo paso del feedback de ambos (imagen)
const BothImageStep = ({ bothImageFeedback, setBothImageFeedback, onSubmit, onBack, imageError, isSubmitting }: {
  bothImageFeedback: string;
  setBothImageFeedback: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  imageError: string;
  isSubmitting: boolean;
}) => (
  <div className="text-center">
    <div className="flex items-center justify-center mb-6">
      <button
        onClick={onBack}
        className="mr-4 p-2 text-gray-600 hover:text-[#44aaf6] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h3 className="text-4xl font-bold text-[#44aaf6]">
        Descríbenos tu problema
      </h3>
    </div>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      Indícanos qué es lo que cambiarías de la imagen, y nos encargamos en unos minutosde editarla o crear una nueva adaptándonos. Cuanto más concretes lo que quieres, ¡mejor será el resultado!
    </p>

    {/* Input para feedback de imagen */}
    <div className="w-full max-w-2xl mx-auto mb-6">
      <label className="block text-left text-sm font-semibold text-gray-700 mb-2">
        ¿Qué cambiarías de la imagen?
      </label>
      <textarea
        placeholder="Ej: Esta imagen es demasiado general, quiero algo más enfocado en mi producto. También quiero colores más vivos y llamativos."
        value={bothImageFeedback}
        onChange={(e) => {
          if (e.target.value.length <= 1000) setBothImageFeedback(e.target.value);
        }}
        maxLength={1000}
        className="w-full p-4 rounded-[2rem] bg-[#e8edf4] border-2 border-transparent focus:border-blue-300 focus:ring-0 outline-none h-40 resize-none placeholder-gray-400 text-gray-800 leading-relaxed"
      />
      {imageError && (
        <p className="text-red-500 text-sm mt-2 text-left">
          {imageError}
        </p>
      )}
    </div>



    <div className="flex flex-col gap-4 justify-center items-center mb-6">
      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className={`px-10 py-3 rounded-full border-2 text-lg font-semibold transition-all ${
          isSubmitting
            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
            : 'border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'
        }`}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar'}
      </button>
    </div>
  </div>
);

// Componente para cuando el usuario no quiere publicar nada hoy
const NoPublishView = () => (
  <div className="text-center">
    <h3 className="text-4xl font-bold text-[#44aaf6] mb-6">
      ¡Entendido!
    </h3>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
      La publicación de hoy no se ha subido a ninguna de tus redes. Recuerda que puedes visitar{' '}
      <a 
        href="https://calendar.siweb.es/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-[#44aaf6] hover:text-blue-600 underline transition-colors"
      >
        https://calendar.siweb.es/
      </a>
      {' '}para preparar las publicaciones como tú consideres.
    </p>
    <p className="text-lg font-bold text-gray-700 leading-relaxed mb-8">
       Ya puedes abandonar esta página.
      </p>
  </div>
);

// Componente para cuando se envía feedback exitosamente
const FeedbackSuccessView = () => (
  <div className="text-center">
    <h3 className="text-4xl font-bold text-[#44aaf6] mb-6">
      ¡Entendido!
    </h3>
    <p className="text-lg text-gray-700 leading-relaxed mb-8">
      Lo volveremos a intentar siguiendo tus instrucciones. En breves recibirás otro correo con una nueva previsualización. Ya puedes abandonar esta página.
    </p>
  </div>
);

interface PreviewData {
  accountName: string;
  imageUrl: string;
  description: string;
  title: string;
  confirmed: boolean;
  user: {
    name: string;
    plan: string;
  };
}

const PreviewScreen = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'initial' | 'feedback' | 'textFeedback' | 'imageFeedback' | 'bothTextStep' | 'bothImageStep' | 'noPublish' | 'feedbackSuccess'>('initial');
  const [textFeedback, setTextFeedback] = useState('');
  const [imageFeedback, setImageFeedback] = useState('');
  const [bothTextFeedback, setBothTextFeedback] = useState('');
  const [bothImageFeedback, setBothImageFeedback] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textFeedbackError, setTextFeedbackError] = useState('');
  const [imageFeedbackError, setImageFeedbackError] = useState('');
  const [bothTextFeedbackError, setBothTextFeedbackError] = useState('');
  const [bothImageFeedbackError, setBothImageFeedbackError] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<'instagram' | 'facebook'>('instagram');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  // Efecto para el fade-in inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Efecto para cargar datos del preview si hay UUID
  useEffect(() => {
    if (uuid) {
      loadPreviewData(uuid);
    }
  }, [uuid]);

  const loadPreviewData = async (previewUuid: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`[DEBUG] Cargando preview para UUID: ${previewUuid}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${previewUuid}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Datos recibidos del backend:`, data);
        setPreviewData(data);
      } else if (response.status === 404) {
        console.log(`[DEBUG] Preview no encontrado (404)`);
        setError('Preview no encontrado');
      } else {
        console.log(`[DEBUG] Error del servidor:`, response.status, response.statusText);
        setError('Error al cargar el preview');
      }
    } catch (err) {
      console.error(`[DEBUG] Error de conexión:`, err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPublication = async () => {
    if (!uuid || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      console.log(`[DEBUG] Frontend: Iniciando publicación para UUID: ${uuid}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${uuid}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: previewData?.title || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Frontend: Respuesta del servidor:`, data);
        
        // Actualizar el estado local
        if (previewData) {
          setPreviewData({ ...previewData, confirmed: true });
        }
        
        // Navegar a ThankYouScreen después del envío exitoso
        navigate('/thank-you');
      } else {
        const errorData = await response.json();
        console.error(`[DEBUG] Frontend: Error del servidor:`, errorData);
        alert(`Error al publicar: ${errorData.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error(`[DEBUG] Frontend: Error de conexión:`, err);
      alert('Error de conexión al publicar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDislike = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('feedback');
      setIsTransitioning(false);
    }, 300);
  };

  const handleImageFeedback = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('imageFeedback');
      setIsTransitioning(false);
    }, 300);
  };

  const handleTextFeedback = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('textFeedback');
      setIsTransitioning(false);
    }, 300);
  };

  const handleBothFeedback = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('bothTextStep');
      setIsTransitioning(false);
    }, 150);
  };

  const handleNoPublish = async () => {
    if (!uuid || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      console.log(`[DEBUG] Frontend: Marcando preview como no publicado para UUID: ${uuid}`);
      // Solo marcamos el preview como resuelto: no se envía nada al webhook de RRSS
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${uuid}/confirm`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Frontend: Respuesta del servidor:`, data);
        
        // Actualizar el estado local
        if (previewData) {
          setPreviewData({ ...previewData, confirmed: true });
        }
        
        // Mostrar la vista de no publicación con transición
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentView('noPublish');
          setIsTransitioning(false);
        }, 300);
      } else {
        const errorData = await response.json();
        console.error(`[DEBUG] Frontend: Error del servidor:`, errorData);
        alert(`Error al marcar como no publicado: ${errorData.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error(`[DEBUG] Frontend: Error de conexión:`, err);
      alert('Error de conexión al marcar como no publicado');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTextFeedback = async () => {
    if (!uuid || isSubmitting) return;
    
    // Validar mínimo de caracteres
    if (textFeedback.trim().length < 5) {
      setTextFeedbackError('Por favor, cuéntanos algo más');
      return;
    }
    
    // Limpiar error si la validación pasa
    setTextFeedbackError('');
    
    setIsSubmitting(true);
    try {
      console.log(`[DEBUG] Frontend: Enviando feedback de texto para UUID: ${uuid}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${uuid}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descriptionNewInstruction: textFeedback,
          title: previewData?.title || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Frontend: Respuesta del servidor:`, data);
        
        // Actualizar el estado local
        if (previewData) {
          setPreviewData({ ...previewData, confirmed: true });
        }
        
        // Mostrar vista de éxito del feedback
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentView('feedbackSuccess');
          setIsTransitioning(false);
        }, 300);
      } else {
        const errorData = await response.json();
        console.error(`[DEBUG] Frontend: Error del servidor:`, errorData);
        alert(`Error al enviar feedback: ${errorData.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error(`[DEBUG] Frontend: Error de conexión:`, err);
      alert('Error de conexión al enviar feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitImageFeedback = async () => {
    if (!uuid || isSubmitting) return;
    
    // Validar mínimo de caracteres
    if (imageFeedback.trim().length < 5) {
      setImageFeedbackError('Por favor, cuéntanos algo más');
      return;
    }
    
    // Limpiar error si la validación pasa
    setImageFeedbackError('');
    
    setIsSubmitting(true);
    try {
      console.log(`[DEBUG] Frontend: Enviando feedback de imagen para UUID: ${uuid}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${uuid}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageNewInstruction: imageFeedback,
          title: previewData?.title || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Frontend: Respuesta del servidor:`, data);
        
        // Actualizar el estado local
        if (previewData) {
          setPreviewData({ ...previewData, confirmed: true });
        }
        
        // Mostrar vista de éxito del feedback
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentView('feedbackSuccess');
          setIsTransitioning(false);
        }, 300);
      } else {
        const errorData = await response.json();
        console.error(`[DEBUG] Frontend: Error del servidor:`, errorData);
        alert(`Error al enviar feedback: ${errorData.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error(`[DEBUG] Frontend: Error de conexión:`, err);
      alert('Error de conexión al enviar feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBothTextNext = () => {
    // Validar mínimo de caracteres para el texto
    if (bothTextFeedback.trim().length < 5) {
      setBothTextFeedbackError('Por favor, cuéntanos algo más');
      return;
    }
    
    // Limpiar error si la validación pasa
    setBothTextFeedbackError('');
    
    // Navegar al siguiente paso
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('bothImageStep');
      setIsTransitioning(false);
    }, 150);
  };

  const handleBothImageBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('bothTextStep');
      setIsTransitioning(false);
    }, 150);
  };

  const handleSubmitBothFeedback = async () => {
    if (!uuid || isSubmitting) return;
    
    // Validar mínimo de caracteres para la imagen
    if (bothImageFeedback.trim().length < 5) {
      setBothImageFeedbackError('Por favor, cuéntanos algo más');
      return;
    }
    
    // Limpiar error si la validación pasa
    setBothImageFeedbackError('');
    
    setIsSubmitting(true);
    try {
      console.log(`[DEBUG] Frontend: Enviando feedback de ambos para UUID: ${uuid}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preview/${uuid}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descriptionNewInstruction: bothTextFeedback,
          imageNewInstruction: bothImageFeedback,
          title: previewData?.title || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[DEBUG] Frontend: Respuesta del servidor:`, data);
        
        // Actualizar el estado local
        if (previewData) {
          setPreviewData({ ...previewData, confirmed: true });
        }
        
        // Mostrar vista de éxito del feedback
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentView('feedbackSuccess');
          setIsTransitioning(false);
        }, 300);
      } else {
        const errorData = await response.json();
        console.error(`[DEBUG] Frontend: Error del servidor:`, errorData);
        alert(`Error al enviar feedback: ${errorData.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error(`[DEBUG] Frontend: Error de conexión:`, err);
      alert('Error de conexión al enviar feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToFeedback = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('feedback');
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackToImageFeedback = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('imageFeedback');
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackToInitial = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('initial');
      setIsTransitioning(false);
    }, 300);
  };



  // Datos de ejemplo para cuando no hay UUID
  const exampleData = {
    imageUrl: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=500&h=500&fit=crop",
    caption: "¡Descubre los sabores únicos de nuestra cocina! 🌟 Cada plato es una experiencia culinaria que te transportará a nuevos sabores. #gastronomía #sabores #experiencia #restaurante",
    username: "mi_restaurante",
    facebookCaption: "¡Descubre los sabores únicos de nuestra cocina! 🌟 Cada plato es una experiencia culinaria que te transportará a nuevos sabores. #gastronomía #sabores #experiencia #restaurante Alargando el texto innecesariamente solo para ver si está funcionando el Ver más en el componente de Facebook"
  };

  // Usar datos reales si están disponibles, sino usar datos de ejemplo
  const currentData = previewData ? {
    imageUrl: previewData.imageUrl,
    caption: previewData.description,
    username: previewData.accountName,
    facebookCaption: previewData.description
  } : exampleData;

  console.log(`[DEBUG] PreviewScreen - currentData:`, currentData);

  return (
    <div
      className={`bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen font-futura text-gray-800 transition-all duration-700 ease-in-out relative ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      style={{ pointerEvents: isLoaded ? 'auto' : 'none' }}
    >
      {/* Efectos de fondo azulados sutiles */}
      <div className="absolute left-10 top-20 w-32 h-24 bg-gradient-to-tr from-blue-200 via-cyan-200 to-transparent opacity-30 rounded-full blur-2xl z-0" style={{ filter: 'blur(40px)' }}></div>
      <div className="absolute right-16 top-40 w-40 h-32 bg-gradient-to-bl from-blue-200 via-cyan-200 to-transparent opacity-25 rounded-full blur-3xl z-0" style={{ filter: 'blur(60px)' }}></div>
      <div className="absolute left-1/3 bottom-20 w-36 h-28 bg-gradient-to-tr from-blue-200 via-cyan-200 to-transparent opacity-20 rounded-full blur-2xl z-0" style={{ filter: 'blur(50px)' }}></div>
      {/* Contenido principal */}
      <div className="px-6 py-4 relative z-10">
        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#44aaf6]"></div>
            <span className="ml-4 text-gray-600">Cargando preview...</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex justify-center items-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold">{error}</p>
              <button 
                onClick={() => uuid && loadPreviewData(uuid)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Contenido principal - solo mostrar si no hay loading ni error */}
        {!loading && !error && (
          <>
            {/* Layout principal - pantalla única */}
            <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto h-[calc(100vh-100px)]">
              {/* Columna izquierda - Preview */}
              <div className="lg:w-1/2 flex flex-col justify-center">
                {/* Título para móviles - solo en vista inicial */}
                {currentView === 'initial' && (
                  <div className="lg:hidden text-center mb-10">
                    <h1 className="text-4xl font-bold text-[#44aaf6]" style={{letterSpacing: '0.02em'}}>
                      ¡Te hemos preparado una publicación para hoy!
                    </h1>
                  </div>
                )}
                
                {/* Selector de redes sociales */}
                <div className="flex gap-4 mb-4 justify-center">
                  <button
                    onClick={() => setSelectedPlatform('instagram')}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                      selectedPlatform === 'instagram'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                       <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                     </svg>
                   </div>
                    <span className="font-semibold">Instagram</span>
                  </button>
                  
                  <button
                    onClick={() => setSelectedPlatform('facebook')}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                      selectedPlatform === 'facebook'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <span className="font-semibold">Facebook</span>
                  </button>
                 </div>
                 
                {/* Preview seleccionada */}
                <div className="flex justify-center">
                  <div className="w-full max-w-sm lg:max-h-[80vh] lg:overflow-y-auto lg:custom-scrollbar">
                    {selectedPlatform === 'instagram' ? (
                 <InstagramPostPreview
                   imageUrl={currentData.imageUrl}
                   caption={currentData.caption}
                   username={currentData.username}
                   postedAtLabel="Ahora"
                   showActions={true}
                   maxCaptionChars={120}
                   fullWidth={true}
                 />
                    ) : (
                 <FacebookPostPreview
                   imageUrl={currentData.imageUrl}
                   caption={currentData.facebookCaption}
                   username={currentData.username}
                   postedAtLabel="Ahora"
                   fullWidth={true}
                 />
                    )}
                  </div>
                </div>
              </div>

              {/* Columna derecha - Contenido dinámico */}
              <div className="lg:w-1/2 flex flex-col justify-center">

            {/* Contenido dinámico - solo mostrar si hay UUID */}
            {uuid && (
                  <div className={`${currentView !== 'initial' ? 'mb-20' : 'mb-10'}`}>
                    <div className={`transition-all duration-300 ease-in-out ${
                      isTransitioning ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
                    }`}>
                      {currentView === 'initial' && (
                        previewData?.confirmed ? (
                          <div className="text-center">
                            <h1 className="text-5xl font-bold text-[#44aaf6] mb-10" style={{letterSpacing: '0.02em'}}>
                              Esta previsualización ya ha sido resuelta.
                            </h1>
                          </div>
                        ) : (
                          <InitialView
                            onConfirm={handleConfirmPublication}
                            onDislike={handleDislike}
                            isConfirmed={false}
                            isSubmitting={isSubmitting}
                          />
                        )
                      )}
                      
                      {currentView === 'feedback' && (
                        <FeedbackOptions
                          onImageFeedback={handleImageFeedback}
                          onTextFeedback={handleTextFeedback}
                          onBothFeedback={handleBothFeedback}
                          onNoPublish={handleNoPublish}
                          onBack={handleBackToInitial}
                          isSubmitting={isSubmitting}
                        />
                      )}
                      
                      {currentView === 'textFeedback' && (
                        <TextFeedback
                          textFeedback={textFeedback}
                          setTextFeedback={setTextFeedback}
                          onSubmit={handleSubmitTextFeedback}
                          onBack={handleBackToFeedback}
                          error={textFeedbackError}
                          isSubmitting={isSubmitting}
                        />
                      )}
                      
                      {currentView === 'imageFeedback' && (
                        <ImageFeedback
                          imageFeedback={imageFeedback}
                          setImageFeedback={setImageFeedback}
                          onSubmit={handleSubmitImageFeedback}
                          onBack={handleBackToFeedback}
                          error={imageFeedbackError}
                          isSubmitting={isSubmitting}
                        />
                      )}
                      
                      {currentView === 'bothTextStep' && (
                        <BothTextStep
                          bothTextFeedback={bothTextFeedback}
                          setBothTextFeedback={setBothTextFeedback}
                          onNext={handleBothTextNext}
                          onBack={handleBackToFeedback}
                          textError={bothTextFeedbackError}
                        />
                      )}
                      
                      {currentView === 'bothImageStep' && (
                        <BothImageStep
                          bothImageFeedback={bothImageFeedback}
                          setBothImageFeedback={setBothImageFeedback}
                          onSubmit={handleSubmitBothFeedback}
                          onBack={handleBothImageBack}
                          imageError={bothImageFeedbackError}
                          isSubmitting={isSubmitting}
                        />
                      )}

                      {currentView === 'noPublish' && (
                        <NoPublishView />
                      )}

                      {currentView === 'feedbackSuccess' && (
                        <FeedbackSuccessView />
                      )}

                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PreviewScreen;
